import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  apiURL = environment.apiURL;

  constructor(private http: HttpClient) {}

  /**
   * Get list of work requests
   */
  getListOfWorkRequests(
    skip?: number,
    limit?: number,
    link?: string,
    assigneeId?: string,
    offerStatus?: string,
    sector?: string,
    search?: string,
    isOfferActive?: boolean,
    isSent?: boolean,
    operationStatus?: string,
  ): Observable<any> {
    let params = new HttpParams().set('skip', skip).set('limit', limit);

    if (sector) params = params.append('sector', sector);
    if (assigneeId) params = params.append('assigneeId', assigneeId);
    if (offerStatus) params = params.append('status', offerStatus);
    if (search) params = params.append('search', search);
    if (operationStatus) params = params.append('operationStatus', operationStatus);
    params = params.append('isActive', isOfferActive);
    params = params.append('isSent', isSent);

    return link === 'offers'
      ? this.http.get(`${this.apiURL}/offers`, { params })
      : this.http.get(`${this.apiURL}/work-requests/${link}`, { params });
  }

  /**
   * Get list of declined work requests
   */
  getListOfUnacceptedJobs(data: {
    skip?: number;
    limit?: number;
    userId?: string;
    rejectionReason?: string;
    declineSource?: string;
    sector?: string;
    search?: string;
  }): Observable<any> {
    let params = new HttpParams().set('skip', data.skip).set('limit', data.limit);

    if (data.userId) params = params.append('userId', data.userId);
    if (data.rejectionReason) params = params.append('rejectionReason', data.rejectionReason);
    if (data.declineSource) params = params.append('declineSource', data.declineSource);
    if (data.sector) params = params.append('sector', data.sector);
    if (data.search) params = params.append('search', data.search);

    return this.http.get(`${this.apiURL}/work-requests/declined`, { params });
  }

  /**
   * Get list of offer work requests
   */
  getListOfOfferWorkRequests(skip?: number, limit?: number, assigneeId?: string, sector?: string, search?: string): Observable<any> {
    let params = new HttpParams().set('skip', skip).set('limit', limit);

    if (sector) params = params.append('sector', sector);
    if (assigneeId) params = params.append('assigneeId', assigneeId);
    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/offers/work-requests`, { params });
  }

  /**
   * Create new work request
   * @param data
   */
  createNewWorkRequest(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests`, data);
  }

  /**
   * Create new offer
   * @param data
   */
  createNewOffer(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/offers`, data);
  }

  /**
   * Edit work request
   * @param data
   * @param workRequestId
   */
  editWorkRequest(data: any, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}`, data);
  }

  /**
   * Edit offer
   * @param data
   * @param offerId
   */
  editOffer(data: any, offerId: string, currency?: string): Observable<any> {
    let params = new HttpParams();

    if (currency === 'rsd') params = params.append('currency', currency);
    return this.http.patch(`${this.apiURL}/offers/${offerId}`, data, { params });
  }

  /**
   * Get work request details
   * @param workRequestId
   */
  getWorkRequestDetails(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}`);
  }

  /**
   * Get offer details
   * @param offerId
   * @param currency
   */
  getOfferDetails(offerId: string, currency?: string): Observable<any> {
    let params = new HttpParams();

    if (currency) params = params.append('currency', currency);

    return this.http.get(`${this.apiURL}/offers/${offerId}`, { params });
  }

  /**
   * Archive work request
   * @param workRequestId
   */
  archiveWorkRequest(workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/archive`, {});
  }

  /**
   * Archive offer
   * @param offerId
   */
  archiveOffer(offerId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/offers/${offerId}/archive`, {});
  }

  /**
   * Archive accepted offer
   * @param offerId
   */
  archiveAcceptedOffer(offerId: string, data: { rejectionReasons: Array<string>; rejectionComment: string }): Observable<any> {
    return this.http.patch(`${this.apiURL}/offers/${offerId}/archive-accepted-offer`, data);
  }

  /**
   * Get work request operations
   * @param workRequestId
   */
  getWorkRequestOperations(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/operations`);
  }

  /**
   * Edit work request operation
   * @param workRequestId
   * @param operationId
   */
  editWorkRequestOperation(data: any, operationId: string, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/operations/${operationId}`, data);
  }

  /**
   * Add work request operation
   * @param workRequestId
   */
  addWorkRequestOperation(data: any, workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/operations`, data);
  }

  /**
   * Change category status
   * @param workRequestId
   */
  changeCategoryStatus(workRequestId: string, type: string, action: string, status: string, sector?: string): Observable<any> {
    const data = {
      status: status,
      sector: '',
    };

    if (sector) {
      data.sector = sector;
    } else {
      delete data.sector;
    }

    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/${type}-list/${action}`, data);
  }

  /**
   * Delete work request operation
   * @param workRequestId
   * @param operationId
   */
  deleteWorkRequestOperation(workRequestId: string, operationId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/operations/${operationId}`);
  }

  /**
   * Operations work request
   * @param workRequestId
   */
  operationsOrder(data: any, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/operations`, data);
  }

  /**
   * Import materials from work request
   * @param workRequestId
   * @param importWorkRequestId
   */
  importMaterialsFromWorkRequest(workRequestId: string, importWorkRequestId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/materials/import/${importWorkRequestId}`, data);
  }

  /**
   * Import operations from work request
   * @param workRequestId
   * @param importWorkRequestId
   */
  importOperationsFromWorkRequest(workRequestId: string, importWorkRequestId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/operations/import/${importWorkRequestId}`, data);
  }

  /**
   * Import external services from previous work request
   * @param workRequestId
   * @param importWorkRequestId
   */
  importExternalServicesFromWorkRequest(workRequestId: string, importWorkRequestId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/external-services/import/${importWorkRequestId}`, data);
  }

  /**
   * Import materials from previous work order
   * @param workRequestId
   * @param workOrderId
   */
  importMaterialsFromWorkOrder(workRequestId: string, workOrderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/materials/import-work-order/${workOrderId}`, {});
  }

  /**
   * Import opeartions from previous work order
   * @param workRequestId
   * @param workOrderId
   */
  importOperationsFromWorkOrder(workRequestId: string, workOrderId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/operations/import-work-order/${workOrderId}`, data);
  }

  /**
   * Get request assignees
   * @param workRequestId
   */
  getWorkRequestAssignees(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/assignees`);
  }

  /**
   * Get request customer service assignees
   * @param workRequestId
   */
  getCustomerServiceWorkRequestAssignees(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/assignees/customer-service`);
  }

  /**
   * Assign order
   * @param workRequestId
   * @param assigneeId
   */
  assignOrder(workRequestId: string, assigneeId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/assignees/${assigneeId}`, data);
  }

  /**
   * Get list of all documents for work request
   * @param workRequestId
   */
  getWorkRequestDocuments(workRequestId: string, isDeliveryAddress?: boolean): Observable<any> {
    let params = new HttpParams();

    if (isDeliveryAddress) params = params.append('isDeliveryAddress', isDeliveryAddress);

    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/documents`, { params });
  }

  /**
   * Add documents for work request
   * @param data
   * @param workRequestId
   */
  addDocumentForWorkRequest(data: FormData, workRequestId: string, isDeliveryAddress?: boolean): Observable<any> {
    let params = new HttpParams();

    if (isDeliveryAddress) params = params.append('isDeliveryAddress', isDeliveryAddress);

    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/documents`, data, { params });
  }

  /**
   * Approve document for work request
   * @param workRequestId
   * @param documentId
   */
  approveDocumentForWorkRequest(workRequestId: string, documentId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/documents/${documentId}/approve`, {});
  }

  /**
   * Delete document for work requests
   * @param workRequestId
   * @param documentId
   */
  deleteDocumentForWorkRequest(workRequestId: string, documentId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/documents/${documentId}`);
  }

  /**
   * Download document
   * @param documentId
   */
  downloadDocument(documentId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/documents/${documentId}/single-download`);
  }

  /**
   * Download all documents for work request
   * @param workRequestId
   */
  downloadAllDocumentsForWorkRequest(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/documents/download`, {
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Edit document for work request
   * @param data
   * @param workRequestId
   * @param documentId
   */
  editDocumentForWorkRequest(data: any, workRequestId: string, documentId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/documents/${documentId}`, data);
  }

  /**
   * Add request sector
   * @param sector
   * @param workRequestId
   */
  addRequestSector(sector: any, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/sectors`, sector);
  }

  /**
   * Add material for work requests
   * @param data
   * @param workRequestId
   */
  addMaterialForWorkRequest(data: any, workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/materials`, data);
  }

  /**
   * Get request materials
   * @param workRequestId
   * @param status
   */
  getRequestMaterials(workRequestId: string, status?: string): Observable<any> {
    let params = new HttpParams();
    if (status) params = params.append('status', status);

    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/materials`, { params });
  }

  /**
   * Edit material for work request
   * @param data
   * @param materialId
   * @param workRequestId
   */
  editMaterialForWorkRequest(data: any, materialId: string, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/materials/${materialId}`, data);
  }

  /**
   * Set custom price for material for work request
   * @param data
   * @param materialId
   * @param workRequestId
   */
  setCustomPrice(data: any, materialId: string, workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/materials/${materialId}/set-custom-price`, data);
  }

  /**
   * Delete custom price for material for work request
   * @param materialId
   * @param workRequestId
   */
  deleteCustomPrice(workRequestId: string, materialId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/materials/${materialId}/delete-custom-price`);
  }

  /**
   * Delete material for work request
   * @param workRequestId
   * @param materialId
   */
  deleteMaterialForWorkRequest(workRequestId: string, materialId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/materials/${materialId}`);
  }

  /**
   * Approve material for work request
   * @param workRequestId
   * @param materialId
   */
  approveMaterialForWorkRequest(workRequestId: string, materialId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/materials/${materialId}/approve`, {});
  }

  /**
   * Change work request status
   * @param workRequestId
   */
  changeWorkRequestStatus(workRequestId: string, status: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/status`, status);
  }

  /**
   * Change offer status
   * @param offerId
   */
  changeOfferStatus(offerId: string, status: any, rejectionReasons?: Array<string>, rejectionComment?: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/offers/${offerId}/status`, { status, rejectionReasons, rejectionComment });
  }

  /**
   * Add new work request specification photos
   * @param workRequestId
   */
  uploadPhoto(formData: any, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/specification-photos`, formData);
  }

  /**
   * Edit work request specification
   * @param workRequestId
   */
  editWorkRequestSpecification(specification: any, workRequestId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/specification`, specification);
  }

  /**
   * Delete work request specification photo
   * @param workRequestId
   */

  deleteWorkRequestSpecificationPhoto(specificationPhoto: string, workRequestId: string): Observable<any> {
    let params = new HttpParams().set('specificationPhoto', specificationPhoto);

    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/specification-photos`, { params });
  }

  /**
   * Create new external service for work request
   * @param workRequestId
   */
  createNewExternalServiceForWorkRequest(data: any, workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/external-services`, data);
  }

  /**
   * Get list of external services for work request
   * @param workRequestId
   */
  getListOfExternalServicesForWorkRequest(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/external-services`);
  }

  /**
   * Edit work request external service
   * @param workRequestId
   * @param workRequestExternalServiceId
   */
  editWorkRequestExternalService(workRequestId: string, workRequestExternalServiceId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/external-services/${workRequestExternalServiceId}`, data);
  }

  /**
   * Delete work request external service
   * @param workRequestId
   * @param workRequestExternalServiceId
   */
  deleteWorkRequestExternalService(workRequestId: string, workRequestExternalServiceId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/external-services/${workRequestExternalServiceId}`);
  }

  /**
   * Get list of previous work requests
   */
  getListOfPreviousWorkRequests(skip: number, limit: number, search?: string): Observable<any> {
    let params = new HttpParams().set('skip', skip).set('limit', limit);

    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/work-requests/import`, { params });
  }

  /**
   * Get calculator parameters
   */
  getCalculatorParameters(): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/calculator-parameters`);
  }

  /**
   * Edit calculator parameters value
   * @param calculatorParameterId
   */
  editCalculatorParameterValue(calculatorParameterId: number, value: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/calculator-parameters/${calculatorParameterId}/value`, { value });
  }

  /**
   * Calculate work request expenses and price
   *  @param workRequestId
   */
  calculateExpensesAndPrices(workRequestId: string, data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/calculator`, data);
  }

  /**
   * Edit work request expenses and price
   * @param workRequestId
   */
  editExpensesAndPrices(workRequestId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/calculator`, data);
  }

  /**
   * Get work request expenses and price
   * @param workRequestId
   */
  getExpensesAndPrices(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/calculator`);
  }

  /**
   * Download offer
   * @param offerId
   */
  downloadOffer(offerId: string, lang: string): Observable<any> {
    let params = new HttpParams();

    if (lang) params = params.append('lang', lang);

    return this.http.get(`${this.apiURL}/offers/${offerId}/download`, {
      params: params,
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Delete work request
   * @param workRequestId
   */
  deleteWorkRequest(workRequestId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}`);
  }

  /**
   * Copy work request
   * @param fromWorkRequestId
   */
  importDataFromWorkRequest(fromWorkRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${fromWorkRequestId}/calculator/reference`);
  }

  /**
   * Create/upload unbounded documents
   */
  createFreeDocuments(data: FormData): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/free-documents`, data);
  }

  /**
   * Connect documents with work request
   * @param workRequestId
   */
  connectDocumentsWithWorkRequest(workRequestId: string, documentsIds: Array<number>): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/free-documents`, { documentsIds });
  }

  /**
   * Delete unbounded document
   * @param documentId
   */
  deleteFreeDocument(documentId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-requests/free-documents/${documentId}`);
  }

  /**
   * Clone work request
   * @param workRequestId
   */
  cloneWorkRequest(workRequestId: string, fromId: string): Observable<any> {
    let params = new HttpParams();

    if (fromId) params = params.append('fromId', fromId);

    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/clone`, null, { params });
  }

  /**
   * Copy work request
   * @param workRequestId
   */
  copyWorkRequest(workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/${workRequestId}/clone`, {});
  }

  /**
   * Download material specification
   * @param workRequestId
   */
  downloadMaterialsSpecification(workRequestId: string): Observable<any> {
    let params = new HttpParams();

    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/materials/download-specifications`, {
      params: params,
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Change status for engineers and graphic artists
   * @param requestId
   */
  changeConfirmationStatusEngineersAndGraphic(requestId: string, status: boolean, statusLabel: string): Observable<any> {
    let params = new HttpParams();

    params = params.append(statusLabel, status);

    return this.http.patch(`${this.apiURL}/work-requests/${requestId}/set-completion`, null, { params });
  }

  /**
   * Get mailing list users
   * @param offerId
   */
  getMailingList(offerId: string, search?: string, isShownChosenUsers?: boolean): Observable<any> {
    let params = new HttpParams();
    if (search) params = params.append('search', search);
    if (isShownChosenUsers) params = params.append('isReadyForNotification', isShownChosenUsers);

    return this.http.get(`${this.apiURL}/offers/${offerId}/notify-users`, { params });
  }

  /**
   * Create list of users which will receive email after offer is accepted
   * @param offerId
   */
  createEmailRecipientList(
    offerId: string,
    users: Array<{
      name: string;
      email: string;
      send: boolean;
      cc: boolean;
    }>,
    areDefault?: boolean,
  ): Observable<any> {
    let params = new HttpParams();
    if (areDefault) params = params.append('areDefault', areDefault);
    return this.http.patch(`${this.apiURL}/offers/${offerId}/notify-users`, { users }, { params });
  }

  /**
   * Add new user to the list of users which will potentially receive email after offer acceptance
   * @param offerId
   */
  addNewRecipientToMailingList(
    offerId: string,
    body: {
      name: string;
      email: string;
      activate: boolean;
    },
  ): Observable<any> {
    return this.http.post(`${this.apiURL}/offers/${offerId}/notify-users`, body);
  }

  /**
   * Send email to chosen users aboute offer acceptance or rejection reason
   */
  sendAcceptanceNotification(offerId: string, files?: FormData, sendRejectionReason?: boolean): Observable<any> {
    let params = new HttpParams();
    if (sendRejectionReason) params = params.append('sendRejectionReason', sendRejectionReason);

    return this.http.post(`${this.apiURL}/offers/${offerId}/send-acceptance-notification`, files, { params });
  }

  /**
   * Return work request from technology to development
   * @param workRequestId
   */
  returnToDevelopment(workRequestId: string, emailMessage: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/technology-to-development`, { emailMessage });
  }

  /**
   * Download calculator
   * @param workRequestId
   */
  downloadCalculator(workRequestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/offers/work-requests/${workRequestId}/calculator-download`, {
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Get income estimation for work orders
   */
  getIncomeEstimationForWorkOrders(data: {
    dateFrom?: string;
    dateTo?: string;
    isInProduction?: boolean;
    search?: string;
    requestType?: string;
  }): Observable<any> {
    let params = new HttpParams();

    if (data.dateFrom) params = params.append('startDate', data.dateFrom);
    if (data.dateTo) params = params.append('endDate', data.dateTo);
    if (data.isInProduction || data.isInProduction === false) params = params.append('isInProduction', data.isInProduction);
    if (data.search) params = params.append('search', data.search);
    if (data.requestType) params = params.append('requestType', data.requestType);

    return this.http.get(`${this.apiURL}/offers/income-overview`, { params });
  }

  /**
   * Create work order production income percent
   */
  createWorkOrderRealizationPercent(data: {
    workOrderId: string;
    projections: Array<{ year: number; month: number; percent: number }>;
  }): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/percentage`, data);
  }

  /**
   * Get income estimation for client
   */
  getIncomeEstimationForClients(): Observable<any> {
    return this.http.get(`${this.apiURL}/clients/monthly-plan`);
  }

  /**
   * Create income estimation for client
   */
  createClientAmountEstimation(data: { clientId: number; month: number; year: number; amount: number }): Observable<any> {
    return this.http.post(`${this.apiURL}/clients/monthly-plan`, data);
  }

  /**
   * Delete client amount estimation
   * @param clientMonthlyPlan
   */
  deleteClientAmountEstimation(clientMonthlyPlan: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/clients/monthly-plan/${clientMonthlyPlan}`);
  }

  /**
   * Get planned budget income
   * @param year
   */
  getPlannedBudgetIncome(year: number): Observable<any> {
    let params = new HttpParams();

    if (year) params = params.append('year', year);

    return this.http.get(`${this.apiURL}/budgets`, { params });
  }

  /**
   * Create budget income
   */
  createBudgetIncome(budgetIncome: Array<{ month: number; year: number; client: number; amount: number }>): Observable<any> {
    return this.http.post(`${this.apiURL}/budgets`, { budgetIncome });
  }

  /**
   * Create sample work request
   * @param data
   */
  createWorkRequestSample(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/sample`, data);
  }

  /**
   * Create reclamation work request
   * @param data
   */
  createWorkRequestReclamation(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-requests/claim`, data);
  }

  /**
   * Get email list for sample work request
   * @param workRequestId
   */
  getMailingListForSampleWorkRequest(
    workRequestId: string,
    search?: string,
    isShownChosenUsers?: boolean,
    notificationReason?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (search) params = params.append('search', search);
    if (isShownChosenUsers) params = params.append('isReadyForNotification', isShownChosenUsers);
    if (notificationReason) params = params.append('notificationReason', notificationReason);

    return this.http.get(`${this.apiURL}/work-requests/${workRequestId}/notify-users`, { params });
  }

  /**
   * Add new user to the list of users which will potentially receive email after creating sample work request
   * @param requestId
   */
  addRecipientToMailingListForSampleWorkRequest(
    requestId: string,
    body: {
      name: string;
      email: string;
      activate: boolean;
    },
    notificationReason?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (notificationReason) params = params.append('notificationReason', notificationReason);
    return this.http.post(`${this.apiURL}/work-requests/${requestId}/notify-users`, body, { params });
  }

  /**
   * Create list of users which will receive email after creating sample work request
   * @param offerId
   */
  createEmailRecipientListForSampleWorkRequest(
    requestId: string,
    users: Array<{
      name: string;
      email: string;
      send: boolean;
      cc: boolean;
    }>,
    notificationReason?: string,
    areDefault?: boolean,
  ): Observable<any> {
    let params = new HttpParams();
    if (notificationReason) params = params.append('notificationReason', notificationReason);
    if (areDefault) params = params.append('areDefault', areDefault);
    return this.http.patch(`${this.apiURL}/work-requests/${requestId}/notify-users`, { users }, { params });
  }

  /**
   * Send email to chosen users about sample work request creation
   */
  sendNotificationAboutSampleWorkRequest(
    requestId: string,
    files?: FormData,
    sendRejectionReason?: boolean,
    notificationReason?: string,
  ): Observable<any> {
    let params = new HttpParams();
    if (sendRejectionReason) params = params.append('sendRejectionReason', sendRejectionReason);
    if (notificationReason) params = params.append('notificationReason', notificationReason);

    return this.http.post(`${this.apiURL}/work-requests/${requestId}/send-sample-notification`, files, { params });
  }

  /**
   * Download yearly income report
   */
  downloadYearlyIncomeReport(): Observable<any> {
    return this.http.get(`${this.apiURL}/incomes/download-report`, { responseType: 'blob', observe: 'body' });
  }

  /**
   * Download declined work requests report
   */
  downloadUnacceptedJobsReport(data: {
    userId?: string;
    rejectionReason?: string;
    declineSource?: string;
    sector?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    fromDate?: string;
    toDate?: string;
  }): Observable<any> {
    let params = new HttpParams();

    if (data.userId) params = params.append('userId', data.userId);
    if (data.rejectionReason) params = params.append('rejectionReason', data.rejectionReason);
    if (data.declineSource) params = params.append('declineSource', data.declineSource);
    if (data.sector) params = params.append('sector', data.sector);
    if (data.search) params = params.append('search', data.search);
    if (data.dateFrom) params = params.append('startDate', data.dateFrom);
    if (data.dateTo) params = params.append('endDate', data.dateTo);
    if (data.fromDate) params = params.append('fromDate', data.fromDate);
    if (data.toDate) params = params.append('toDate', data.toDate);

    return this.http.get(`${this.apiURL}/work-requests/declined/download-report`, {
      params,
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Download offers report
   * @param dateFrom
   * @param dateTo
   */
  downloadOffersReport(dateFrom: string, dateTo: string): Observable<any> {
    let params = new HttpParams();
    if (dateFrom) params = params.append('startDate', dateFrom);
    if (dateTo) params = params.append('endDate', dateTo);

    return this.http.get(`${this.apiURL}/offers/income-report/download-report`, {
      params,
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Decline work request
   * @param workRequestId
   * @param data
   */
  declineWorkRequest(
    workRequestId: string,
    data: {
      expectedIncome: number;
      productCategories: Array<{ name: string }>;
      materialType: string;
      rejectionReasons: Array<string>;
      rejectionComment: string;
    },
  ): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/decline`, data);
  }

  /**
   * Delete email recipients on offer
   */
  deleteEmailRecipientOnOffer(offerId: string, email: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/offers/${offerId}/notify-users`, { body: { email } });
  }

  /**
   * Delete email recipients on sample work request
   */
  deleteEmailRecipientOnSampleWorkRequest(workRequestId: string, email: string, notificationReason?: string): Observable<any> {
    let params = new HttpParams();
    if (notificationReason) params = params.append('notificationReason', notificationReason);
    return this.http.delete(`${this.apiURL}/work-requests/${workRequestId}/notify-users`, { body: { email }, params });
  }

  // Generate an additional work order from the work requests in the accepted offer
  createAdditionalWorkOrders(offerId: string, workRequestId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/offers/${offerId}/work-requests/${workRequestId}/create-work-order`, {});
  }

  /**
   * Link work request to work order
   */
  linkWorkRequestToWorkOrder(workRequestId: string, workOrderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/work-orders/${workOrderId}/link`, {});
  }

  /**
   * Get activity logs for work request basic information
   */
  getWorkRequestActivityLogs(requestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${requestId}/activities`);
  }

  /**
   * Get activity logs for work request materials
   */
  getWorkRequestMaterialsActivityLogs(requestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${requestId}/materials/activities`);
  }

  /**
   * Get activity logs for work request operations
   */
  getWorkRequestOperationsActivityLogs(requestId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-requests/${requestId}/operations/activities`);
  }

  /**
   * Set temporary values to the calculator related to the changed quantity
   */
  setTepmCalculatorValues(
    workRequestId: string,
    data: { count: number; transportationCost: number; additionalWork: number },
  ): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/set-temp-values`, data);
  }

  /**
   * Set work request complexity level
   */
  setWorkRequestComplexityLevel(workRequestId: string, complexity: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-requests/${workRequestId}/complexity`, { complexity });
  }
}
